import '../css/bell.scss';
import {emptyStateHtml, loadingStateHtml} from '../../common/js/empty-state';
import {displayError} from '../../common/js/feature-toast';
import {fetchUnreadCount, listNotifications, markAllNotificationsRead, markNotificationRead} from './api';
import {
    badgeText, COUNT_EVENT, dispatchCount, dispatchRead, isSafeUrl, isStrayClick, loadErrorHtml, markAllIconHtml,
    markItemElementRead, notificationItemHtml, pageLanguage, tr,
} from './render';

export const POLL_INTERVAL_MS = 60 * 1000;
const RETURN_REFRESH_INTERVAL_MS = 15 * 1000;
const DROPDOWN_LIMIT = 10;

export function initInAppBell(i18nReady = Promise.resolve()) {
    const root = document.querySelector('[data-inapp-bell]');
    if (!root) {
        return;
    }
    const button = root.querySelector('[data-inapp-bell-button]');
    const badge = root.querySelector('[data-inapp-bell-badge]');
    const panel = root.querySelector('[data-inapp-bell-panel]');
    const list = root.querySelector('[data-inapp-bell-list]');
    const markAllButton = root.querySelector('[data-inapp-mark-all]');
    const viewAllLink = root.querySelector('[data-inapp-view-all]');
    const lang = pageLanguage();
    const state = {
        count: 0,
        // Bumped when a count-bearing request starts or a count event arrives: only the newest count is shown.
        countSeq: 0,
        items: [],
        listState: 'idle', // then 'loading', 'ready' or 'error'
        markingAll: false,
        timer: null,
        lastFetchAt: 0,
        stopped: false,
        request: 0,
    };

    function updateMarkAll() {
        // "Mark all as read" is bounded by the newest loaded id, so it waits for a loaded list with items.
        markAllButton.disabled = state.markingAll || state.listState !== 'ready' || !state.items.length
            || state.count === 0;
    }

    // A control that disables itself drops the keyboard focus to the page body.
    function focusFellFrom(control) {
        const active = document.activeElement;
        return !active || active === document.body || active === control;
    }

    function showCount(count) {
        state.count = Math.max(0, Number(count) || 0);
        const text = badgeText(state.count);
        badge.textContent = text;
        badge.hidden = !text;
        button.setAttribute('aria-label', state.count
            ? tr('common:notifications.openLabelUnread', 'Notifications, unread: {{count}}', {count: state.count})
            : tr('common:notifications.title', 'Notifications'));
        updateMarkAll();
    }

    // A response's count is shown only when it is a number and no newer count-bearing request has started.
    function applyServerCount(count, seq) {
        if (state.stopped || seq !== state.countSeq || !Number.isFinite(count)) {
            return false;
        }
        showCount(count);
        return true;
    }

    function stopPolling() {
        state.stopped = true;
        clearTimeout(state.timer);
        showCount(0);
    }

    async function refreshCount() {
        if (state.stopped) {
            return;
        }
        state.lastFetchAt = Date.now();
        const seq = ++state.countSeq;
        try {
            const data = await fetchUnreadCount();
            if (data.signedOut) {
                stopPolling();
                return;
            }
            applyServerCount(data.unread_count, seq);
        } catch (error) {
            // A failed poll, or one aborted after UNREAD_COUNT_TIMEOUT_MS, stays silent; the next one retries.
        }
    }

    function schedulePoll() {
        clearTimeout(state.timer);
        if (state.stopped || document.visibilityState !== 'visible') {
            return;
        }
        state.timer = setTimeout(async () => {
            await refreshCount();
            schedulePoll();
        }, POLL_INTERVAL_MS);
    }

    function renderList() {
        list.innerHTML = state.items.length
            ? state.items.map((item) => notificationItemHtml(item, {lang})).join('')
            : emptyStateHtml({title: tr('common:notifications.empty', 'No notifications yet'), compact: true});
    }

    async function loadList() {
        const request = ++state.request;
        const seq = ++state.countSeq;
        state.listState = 'loading';
        updateMarkAll();
        list.innerHTML = loadingStateHtml({title: tr('common:loading', 'Loading'), compact: true});
        try {
            const data = await listNotifications({lang, limit: DROPDOWN_LIMIT});
            if (request !== state.request) {
                return;
            }
            state.items = data.items || [];
            state.listState = 'ready';
            applyServerCount(data.unread_count, seq);
            updateMarkAll();
            renderList();
        } catch (error) {
            if (request !== state.request) {
                return;
            }
            state.listState = 'error';
            updateMarkAll();
            list.innerHTML = loadErrorHtml({compact: true});
        }
    }

    function openPanel() {
        panel.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        document.body.classList.add('inapp-bell-open');
        loadList();
    }

    function closePanel({restoreFocus = false} = {}) {
        if (panel.hidden) {
            return;
        }
        panel.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('inapp-bell-open');
        if (restoreFocus) {
            button.focus();
        }
    }

    function markItemRead(item) {
        if (!item || item.is_read) {
            return;
        }
        item.is_read = true;
        showCount(state.count - 1);
        dispatchCount(state.count);
        const seq = ++state.countSeq;
        markNotificationRead(item.id, {keepalive: true})
            .then((data) => {
                // The page keeps a read it is told about, so it hears of one only after the server has it.
                dispatchRead({id: item.id});
                if (applyServerCount(data?.unread_count, seq)) {
                    dispatchCount(state.count);
                }
            })
            .catch(() => {
                // The optimistic count stays until the next poll.
            });
    }

    button.addEventListener('click', (event) => {
        // The bots bundle deletes an answer group on a delegated click on any .icon-btn
        // (src/bots/js/events.js); stopping the click here keeps the bell out of that handler.
        event.stopPropagation();
        if (panel.hidden) {
            openPanel();
        } else {
            closePanel();
        }
    });

    // Capture phase, so a page handler that stops the click cannot keep the panel open. composedPath() still
    // contains the root when the list re-renders the clicked element (Retry, an item without a link).
    document.addEventListener('click', (event) => {
        if (!panel.hidden && !event.composedPath().includes(root)) {
            closePanel();
        }
    }, true);

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !panel.hidden) {
            closePanel({restoreFocus: true});
        }
    });

    list.addEventListener('click', (event) => {
        if (event.target.closest('[data-inapp-retry]')) {
            loadList();
            // Retry itself is gone with the re-render; the focus stays in the panel.
            list.focus({preventScroll: true});
            return;
        }
        const element = event.target.closest('[data-inapp-item]');
        if (!element) {
            return;
        }
        const onLink = event.target.closest('a[href]');
        if (!onLink && isStrayClick(event)) {
            return;
        }
        markItemRead(state.items.find((item) => item.id === Number(element.dataset.inappItem)));
        if (onLink) {
            // The browser follows the clicked link. A new tab or a modified click keeps this page, where the
            // item then shows as read; updating it in place keeps the link and its focus.
            markItemElementRead(element);
            return;
        }
        const url = element.dataset.inappUrl;
        if (url && isSafeUrl(url)) {
            window.location.assign(url);
        } else {
            renderList();
        }
    });

    // A middle click opens a link in a new tab and fires auxclick instead of click; the item still counts as read.
    list.addEventListener('auxclick', (event) => {
        const element = event.target.closest('[data-inapp-item]');
        if (event.button !== 1 || !element || !event.target.closest('a[href]')) {
            return;
        }
        markItemRead(state.items.find((item) => item.id === Number(element.dataset.inappItem)));
        markItemElementRead(element);
    });

    markAllButton.addEventListener('click', async () => {
        const upToId = state.items.reduce((max, item) => Math.max(max, item.id), 0);
        // Without a loaded id the request would mark everything, including notifications nobody has seen.
        if (!upToId) {
            return;
        }
        const hadFocus = document.activeElement === markAllButton;
        // An older list response cannot overwrite the result.
        const request = ++state.request;
        const seq = ++state.countSeq;
        state.markingAll = true;
        updateMarkAll();
        try {
            const data = await markAllNotificationsRead(upToId);
            // The server has read everything up to the bound, whether or not this list is still the one shown.
            dispatchRead({upToId});
            if (request === state.request) {
                state.items.forEach((item) => {
                    item.is_read = true;
                });
                renderList();
            }
            if (applyServerCount(data?.unread_count, seq)) {
                dispatchCount(state.count);
            }
        } catch (error) {
            displayError(tr('common:notifications.actionError', 'Could not update notifications'));
        } finally {
            state.markingAll = false;
            updateMarkAll();
            // The button disabled itself, which drops the keyboard focus to the page body.
            if (hadFocus && focusFellFrom(markAllButton)) {
                list.focus({preventScroll: true});
            }
        }
    });

    // A count from the notifications page, or from this bell, is the newest count.
    document.addEventListener(COUNT_EVENT, (event) => {
        const count = event.detail?.count;
        if (state.stopped || !Number.isFinite(count)) {
            return;
        }
        state.countSeq += 1;
        showCount(count);
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') {
            clearTimeout(state.timer);
            return;
        }
        if (Date.now() - state.lastFetchAt >= RETURN_REFRESH_INTERVAL_MS) {
            refreshCount();
        }
        schedulePoll();
    });

    i18nReady.catch(() => null).then(() => {
        // An icon with the label as its name and tooltip: the text wrapped in the narrow header in long languages.
        const markAllLabel = tr('common:notifications.markAllRead', 'Mark all as read');
        markAllButton.innerHTML = markAllIconHtml();
        markAllButton.setAttribute('aria-label', markAllLabel);
        markAllButton.title = markAllLabel;
        viewAllLink.textContent = tr('common:notifications.viewAll', 'All notifications');
        showCount(state.count);
        refreshCount().then(schedulePoll);
    });
}
